import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { Button, ChevronRightIcon, ChevronLeftIcon, SpreadDotsIcon } from '@ag1/ui';
import clsx from 'clsx';

import styles from './Pagination.module.scss';

type PaginationPagesProps = {
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalItemsCount: number;
  itemsPerPage: number;
  onPageChange?: (index?: number) => void;
};

export const Pagination = ({
  currentPage,
  setCurrentPage,
  totalItemsCount,
  itemsPerPage,
  onPageChange
}: PaginationPagesProps) => {
  // Build the pages array
  const totalPages = Math.ceil(totalItemsCount / itemsPerPage);
  const paginationPages = useMemo(() => {
    if (totalPages <= 1) return [];
    if (totalPages <= 6) return [Array.from({ length: totalPages }).map((_, idx) => idx + 1)];

    if (currentPage <= 2 || currentPage >= totalPages - 1)
      return [
        Array.from({ length: 3 }).map((_, idx) => idx + 1),
        null,
        Array.from({ length: 3 }).map((_, idx) => totalPages - 2 + idx)
      ];

    const midPages: (number | null)[] = [
      ...Array.from({ length: 3 }).map((_, idx) => currentPage + (idx - 1))
    ];
    if (currentPage - 1 > 2) midPages.unshift(null);
    if (currentPage < totalPages - 2) midPages.push(null);

    return [[1], midPages, [totalPages]];
  }, [totalPages, currentPage]);

  const changePage = (index: number) => {
    if (index < 1 || index > totalPages) return;
    setCurrentPage(index);
    onPageChange?.(index);
  };

  if (!paginationPages.length) return null;

  return (
    <div className={styles.paginationContainer}>
      <Button
        className={clsx(styles.pageBtn, currentPage <= 1 ? styles.disabledBtn : '')}
        variant="outline"
        theme="new"
        size="small"
        aria-label="Previous Page Button"
        onClick={() => changePage(currentPage - 1)}>
        <ChevronLeftIcon />
      </Button>
      {paginationPages.map((pageItem) => {
        if (pageItem === null)
          return (
            <Button
              className={styles.pageBtn}
              variant="outline"
              theme="new"
              size="small"
              key={uuid()}>
              <SpreadDotsIcon />
            </Button>
          );

        return pageItem.map((pageNumber) => {
          if (pageNumber === null)
            return (
              <Button
                className={styles.pageBtn}
                variant="outline"
                theme="new"
                size="small"
                key={uuid()}>
                <SpreadDotsIcon />
              </Button>
            );
          return (
            <Button
              className={styles.pageBtn}
              variant={currentPage === pageNumber ? 'primary' : 'outline'}
              theme="new"
              size="small"
              onClick={() => changePage(pageNumber)}
              key={uuid()}>
              {pageNumber}
            </Button>
          );
        });
      })}
      <Button
        className={clsx(styles.pageBtn, currentPage >= totalPages ? styles.disabledBtn : '')}
        variant="outline"
        theme="new"
        size="small"
        aria-label="Next Page Button"
        onClick={() => changePage(currentPage + 1)}>
        <ChevronRightIcon />
      </Button>
    </div>
  );
};
